// Un-vetted contractors waiting at a kiosk — RON-5469. Prototype region: `#appr-alert`.
//
// A LIST, since Phase H (22-October-2026). The first build carried one request, because two
// un-vetted contractors in a lobby at once seemed rare and a stack of banners would push the
// board off the screen. The server sends an array, and the case is less rare than it looked: a
// kiosk can raise one while a receptionist is deciding another.
//
// So: ONE banner, naming the first request, with a count when there are more. The board does not
// get pushed down, nothing is hidden, and the dialog is where the queue is worked through.
//
// WHERE IT SITS. Between the date bar and the stats tiles, as the prototype has it. Under the
// bar that says which day is on screen, because the banner is only ever about today; above the
// tiles, because somebody is waiting in a lobby and the tiles are not urgent.

import { useTranslation } from "react-i18next";

import { formatApprovalSubject, pluralText } from "../receptionConstants";

const ReceptionApprovalAlert = ({ approvals = [], onReview }) => {
  const { t } = useTranslation("common");

  if (!approvals.length) return null;

  // The oldest request is the one named, because it is the person who has been standing there
  // longest. The list arrives in `requested_at` order.
  const [first] = approvals;
  const others = approvals.length - 1;

  const who = formatApprovalSubject(first, { brackets: true });

  // The server's own summary strings. We do not rebuild this from `outstanding` — that list is
  // for the dialog, where each line is ticked individually.
  //
  // `host_notified` is ALWAYS false from this endpoint ("a dashboard alert only, no email to the
  // host"), so the banner no longer claims the host was told. It used to, on the strength of a
  // flag we invented, and it was never true.
  const detail = first.outstandingLabels.join(" · ");

  return (
    <section className="reception-approval-alert" role="alert">
      <span className="reception-approval-alert__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </span>

      <div className="reception-approval-alert__text">
        <p className="reception-approval-alert__title">
          {t("react_reception_approval_alert_title").replace("%s", who)}
        </p>
        {detail && <p className="reception-approval-alert__detail">{detail}</p>}
        {/* Only when there are more. A count of zero said out loud is worse than silence. */}
        {others > 0 && (
          <p className="reception-approval-alert__more">
            {pluralText(t, "react_reception_approval_more_waiting", others)}
          </p>
        )}
      </div>

      <button type="button" className="reception-btn" onClick={onReview}>
        {t("react_reception_approval_review")}
      </button>
    </section>
  );
};

export default ReceptionApprovalAlert;
